import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, PenLine } from "lucide-react";
import { Seo, articleSchema } from "../lib/seo";
import { fetchArticle, fetchArticles, formatDate } from "../lib/api";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "../components/motion";
import { DraftBadge, ArticleCard } from "../components/bits";
import { CATEGORIES } from "./Insights";

const Block = ({ block }) => {
  if (block.type === "h2") return <h2>{block.text}</h2>;
  if (block.type === "list") return <ul>{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
  if (block.type === "callout") return <p className="callout">{block.text}</p>;
  return <p>{block.text}</p>;
};

export const InsightPost = () => {
  const { slug } = useParams();
  const { data: article, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => fetchArticle(slug),
    retry: false,
  });
  const { data: all = [] } = useQuery({
    queryKey: ["articles"],
    queryFn: () => fetchArticles(),
  });

  if (isLoading)
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-navy pt-24">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />
      </div>
    );

  if (isError || !article)
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-navy pt-24 text-center">
        <h1 className="font-serif text-4xl text-white">Article not found</h1>
        <p className="mt-4 text-white/60">This piece may have been moved or removed.</p>
        <Link to="/insights" className="btn-gold mt-8">
          Back to Insights
        </Link>
      </div>
    );

  const related = all
    .filter((a) => a.slug !== slug && a.category === article.category)
    .concat(all.filter((a) => a.slug !== slug && a.category !== article.category))
    .slice(0, 3);

  return (
    <>
      <Seo
        title={article.title}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        type="article"
        jsonLd={articleSchema(article)}
      />

      <article>
        <header className="relative overflow-hidden bg-navy pb-20 pt-40 sm:pt-48">
          <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
          <div className="absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" aria-hidden="true" />
          <div className="container-x relative max-w-4xl">
            <Reveal y={16}>
              <Link
                to="/insights"
                data-testid="article-back-link"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-gold"
              >
                <ArrowLeft size={13} /> All insights
              </Link>
            </Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold">
                {article.category}
              </span>
              <DraftBadge />
            </div>
            <MaskedLines
              lines={[article.title]}
              className="mt-6 text-3xl font-medium leading-[1.15] tracking-tight text-white sm:text-5xl"
              delay={0.1}
            />
            <Reveal delay={0.5} className="mt-8 flex flex-wrap gap-6 font-mono text-[11px] uppercase tracking-[0.16em] text-white/45">
              <span className="flex items-center gap-2">
                <CalendarDays size={13} className="text-gold" /> {formatDate(article.published_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={13} className="text-gold" /> {article.read_minutes} min read
              </span>
              <span className="flex items-center gap-2">
                <PenLine size={13} className="text-gold" /> {article.author}
              </span>
            </Reveal>
          </div>
        </header>

        <div className="border-b border-line bg-cream">
          <div className="container-x max-w-4xl py-4">
            <p className="text-xs leading-relaxed text-steel">
              <span className="font-semibold uppercase tracking-wider text-gold-dark">Draft:</span>{" "}
              sample article prepared for review — publication-ready content will be
              finalised with the practice before it goes live.
            </p>
          </div>
        </div>

        <div className="bg-white py-20 sm:py-24">
          <div className="container-x max-w-3xl">
            <p className="font-serif text-xl leading-relaxed text-ink sm:text-2xl">
              {article.excerpt}
            </p>
            <div className="prose-legal mt-10">
              {article.content.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </div>
        </div>

        <section className="bg-canvas py-20 sm:py-24">
          <div className="container-x">
            <div className="flex items-end justify-between gap-6">
              <h2 className="text-3xl font-medium tracking-tight text-ink sm:text-4xl">Related reading</h2>
              <Link to="/insights" className="hidden items-center gap-2 text-sm font-semibold text-navy hover:text-gold-dark sm:inline-flex">
                All insights <ArrowRight size={15} />
              </Link>
            </div>
            <Stagger className="mt-10 grid gap-6 md:grid-cols-3">
              {related.map((a, i) => (
                <StaggerItem key={a.slug} className="h-full">
                  <ArticleCard article={a} index={i} />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      </article>
    </>
  );
};

export default InsightPost;
